import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Group,
  Image,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Save } from 'lucide-react';
import {
  fetchUserCustomAudio,
  fetchUserLinkableVideos,
  syncUserCustomAudioVideos,
  type LinkableVideo,
  type UserCustomAudioGroup,
} from '../../services/userAudiosManager';
import { getProductIconUrl, PRODUCT_ICONS } from '../../types/contants';

type UserCustomAudioDetailProps = {
  userId: string;
  audioId: string;
};

const SUITE_TO_ICON: Record<string, keyof typeof PRODUCT_ICONS> = {
  'Augmented Wellbeing': 'wellbeing',
  'Augmented Psychology': 'psychology',
  'Augmented Corporate': 'corporate',
  'Augmented Situations': 'situations',
  'Augmented Mentalization': 'mentalization',
  'Augmented Performance': 'performance',
  'Augmented Exposure': 'exposure',
};

function suiteIconUrl(suiteType: string | null | undefined): string | null {
  if (!suiteType) return null;
  const key = SUITE_TO_ICON[suiteType];
  if (!key) return null;
  return getProductIconUrl(key);
}

export const UserCustomAudioDetail = memo(function UserCustomAudioDetail({
  userId,
  audioId,
}: UserCustomAudioDetailProps) {
  const [group, setGroup] = useState<UserCustomAudioGroup | null>(null);
  const [linkable, setLinkable] = useState<LinkableVideo[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [audioGroup, videos] = await Promise.all([
        fetchUserCustomAudio(userId, audioId),
        fetchUserLinkableVideos(userId),
      ]);
      const byId = new Map(videos.map(v => [v.id, v]));
      for (const v of audioGroup.videos) {
        if (!byId.has(v.id)) {
          byId.set(v.id, {
            id: v.id,
            name: v.name,
            category: v.category,
            product_id: v.product_id ?? 0,
            product_name: v.product_name ?? '',
            suite_type: v.suite_type,
          });
        }
      }
      setGroup(audioGroup);
      setLinkable([...byId.values()]);
      setSelectedIds(audioGroup.videos.map(v => v.id));
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile caricare il dettaglio audio',
      });
    } finally {
      setLoading(false);
    }
  }, [audioId, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const initialIds = useMemo(
    () => new Set(group?.videos.map(v => v.id) ?? []),
    [group]
  );

  const dirty = useMemo(() => {
    if (selectedIds.length !== initialIds.size) return true;
    return selectedIds.some(id => !initialIds.has(id));
  }, [initialIds, selectedIds]);

  const toggleVideo = useCallback((videoId: number, checked: boolean) => {
    setSelectedIds(prev =>
      checked
        ? prev.includes(videoId)
          ? prev
          : [...prev, videoId]
        : prev.filter(id => id !== videoId)
    );
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const updated = await syncUserCustomAudioVideos(
        userId,
        audioId,
        selectedIds
      );
      setGroup(updated);
      setSelectedIds(updated.videos.map(v => v.id));
      notifications.show({
        color: 'green',
        title: 'Salvato',
        message: `${updated.videos.length} video collegati`,
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Salvataggio fallito',
        message: error instanceof Error ? error.message : 'Errore sconosciuto',
      });
    } finally {
      setSaving(false);
    }
  }, [audioId, selectedIds, userId]);

  if (loading) {
    return (
      <Group justify='center' py='xl'>
        <Loader />
      </Group>
    );
  }

  if (!group) {
    return <Text c='dimmed'>Audio non trovato.</Text>;
  }

  return (
    <Stack gap='lg'>
      <Group justify='space-between' align='flex-start'>
        <Stack gap={4}>
          <Title order={3}>{group.name}</Title>
          <Text size='sm' c='dimmed'>
            Lingua: {group.lang}
            {group.size != null ? ` · ${Math.round(group.size / 1024)} KB` : ''}
          </Text>
          <Text
            size='xs'
            c='dimmed'
            style={{
              maxWidth: 560,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={group.url}
          >
            {group.url}
          </Text>
        </Stack>
        <Button
          leftSection={<Save size={16} />}
          disabled={!dirty}
          loading={saving}
          onClick={() => void handleSave()}
        >
          Salva collegamenti
        </Button>
      </Group>

      <Stack gap='sm'>
        <Text fw={600}>Video collegabili</Text>
        <Text size='sm' c='dimmed'>
          Video 3D visibili nell&apos;account di questo utente.
        </Text>
        {linkable.length === 0 ? (
          <Text c='dimmed'>
            Nessun video disponibile per i prodotti utente.
          </Text>
        ) : (
          <Stack gap='xs'>
            {linkable.map(video => {
              const iconUrl = suiteIconUrl(video.suite_type);
              const checked = selectedIds.includes(video.id);
              return (
                <Checkbox
                  key={video.id}
                  checked={checked}
                  onChange={event =>
                    toggleVideo(video.id, event.currentTarget.checked)
                  }
                  label={
                    <Group gap='sm' wrap='nowrap'>
                      {iconUrl ? (
                        <Image src={iconUrl} w={22} h={22} fit='contain' />
                      ) : null}
                      <Text size='sm'>
                        {video.name}
                        {video.product_name ? ` — ${video.product_name}` : ''}
                      </Text>
                      {video.category ? (
                        <Badge size='sm' variant='light'>
                          {video.category}
                        </Badge>
                      ) : null}
                    </Group>
                  }
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
});
